<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Общие настройки</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте заполним общие настройки вашего проекта.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Данные проекта&raquo;. На&nbsp;появившейся странице
              выберите &laquo;Общие настройки&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="osnovnaya-informacija"
            class="typography__headline2"
          >
            Основная информация
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;основной информации можно заполнить текстовое описание вашего проекта и&nbsp;загрузить его логотип.
            </p>

            <p>Эти элементы будут использоваться в&nbsp;созданном сайте, поэтому важно их&nbsp;заполнить.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="galeriya"
            class="typography__headline2"
          >
            Галерея
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;галерею проекта можно загрузить до&nbsp;10&nbsp;изображений в&nbsp;разрешении Jpeg, Jpg или Png,
              с&nbsp;максимальным весом 5&nbsp;Мб. Загружать можно разом несколько картинок.
            </p>

            <p>Эти элементы будут использоваться в&nbsp;созданном сайте, поэтому важно их&nbsp;заполнить.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="ydalenie-proekta"
            class="typography__headline2"
          >
            Удаление проекта и сохранение изменений
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              Думаю, удалять новый проект пока нет необходимости. Но&nbsp;если такая потребность появится&nbsp;&mdash;
              вы&nbsp;будете знать где это сделать.
            </p>

            <p>
              Как заполните всю нужную информацию, нажмите &laquo;Сохранить&raquo; в&nbsp;самом низу страницы. После
              успешного сохранения увидите в&nbsp;правом верхнем углу уведомление.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/obshchiye-nastroyki-6.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;общими настройками разобрались. Самое время перейти к&nbsp;заполнению <BaseLink target="_blank" to="faq/articles/kontakty-i-dannye-filialov">контактов и&nbsp;данных
            филиалов</BaseLink>.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Данные проекта",
    url: "/faq/categories/dannyye-proyekta",
  },
  {
    name: "Общие настройки",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Основная&nbsp;информация",
    url: "osnovnaya-informacija",
  },
  {
    name: "Галерея",
    url: "galeriya",
  },
  {
    name: "Удаление&nbsp;проекта&nbsp;и&nbsp;сохранение изменений",
    url: "ydalenie-proekta",
  },
];

const relatedArticles = [
  {
    name: "Настройка меню CRM",
    slug: "nastroyka-menyu-crm",
  },
  {
    name: "Контакты и&nbsp;данные филиалов",
    slug: "kontakty-i-dannye-filialov",
  },
  {
    name: "Язык и&nbsp;регион",
    slug: "yazyk-i-region",
  },
  {
    name: "Уведомления",
    slug: "uvedomleniya",
  },
];

useSeoMeta({
  title: "Общие настройки",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
